import { useGetSuperAdminAuditLog } from '@agos/api-client/platform'
import { T, formatDateTime, tt } from '@agos/shared'
import { useMemo } from 'react'
import { PageHeader, type Column, DataTable, EmptyState, ReadFailed, Spinner, StatusBadge } from '@agos/ui'
import { ScrollText } from 'lucide-react'

import type { AuditEvent } from './audit-log'
import {
  actionLabel,
  actionTone,
  auditEventsOf,
  auditTotals,
  changeNote,
  changes,
  isFirstSet,
  targetLabel,
  truncationNote,
  actorLabel,
} from './audit-log'

/**
 * What has been done to the platform, and by whom - `SAAS.md` §3.12.
 *
 * Every row names a real administrator's e-mail address. It is shown, because that is the point of an
 * audit trail - but nothing here is quoted in a test fixture, and the e2e assert on shape and count.
 *
 * **`before` and `after` are on every row and this page showed neither**, so it recorded *that*
 * something happened and not **what changed** - the one question an audit trail exists to answer.
 * The owner's two newest events are the same `assign_package` on the same franchise eight seconds
 * apart; without the diff they are two identical rows.
 *
 * **`actorName` is deliberately not shown**: it is `"Control Tower"` on all 45 rows - the system's
 * name, not a person's - so the e-mail is the actor and the name would add a column of noise.
 */
export function AuditLogPage() {
  const { data, isLoading, isError } = useGetSuperAdminAuditLog()
  const events = useMemo(() => auditEventsOf(data), [data])
  const totals = useMemo(() => auditTotals(data), [data])
  const truncated = truncationNote(events.length, totals)

  /*
   * The columns - §3.92.
   *
   * Sortable on the three an auditor arrives with: when, who, and which action. **Not on the change
   * note** - it is prose assembled from a diff, and ordering rows by the alphabet of their
   * descriptions answers nothing.
   *
   * *Kedy* carries the raw timestamp as its sort value rather than the formatted string, because
   * `formatDateTime` is Slovak day-first and sorting that text puts the 2nd of January before the
   * 1st of February.
   */
  const columns: Column<AuditEvent>[] = [
    {
      key: 'action',
      header: tt('Akcia'),
      sortable: true,
      /* FB-961, §3.187 - sorted on the **label**, or `Zmazané` and `Zmenené moduly` interleave by the alphabet
         of their English keys, which is not the order the column shows. */
      sortValue: (event) => actionLabel(event.action),
      /* FB-961, §3.187 - **this printed `event.action` raw**, so an operator read `update_modules`,
         `clear_login` and `set_password`; legacy spends one line de-underscoring it. And the badge is coloured
         by what the action does to the data, because thirteen uncoloured verbs make a deletion look like an
         edit on the one screen somebody opens to find a deletion. */
      render: (event) => (
        <span data-audit-action data-audit-tone={actionTone(event.action)}>
          <StatusBadge label={actionLabel(event.action)} color={actionTone(event.action)} />
        </span>
      ),
    },
    {
      key: 'target',
      header: tt('Cieľ'),
      render: (event) => targetLabel(event),
    },
    {
      key: 'change',
      header: tt('Čo sa zmenilo'),
      className: 'text-[11px]',
      render: (event) => <span data-audit-change>{changeNote(event)}</span>,
    },
    {
      key: 'actor',
      header: tt('Kto'),
      className: 'text-[11px] text-muted-foreground',
      sortable: true,
      // Sorted on what the column shows, or *systém* and the names interleave by a hidden field.
      sortValue: (event) => actorLabel(event),
      // §3.114 - the name, with the e-mail as the fallback and *systém* below that, which is legacy's
      // own order. `actorEmail ?? ''` left the cell **empty** on every row a scheduled job wrote.
      render: (event) => <span data-audit-actor>{actorLabel(event)}</span>,
    },
    {
      key: 'when',
      header: tt('Kedy'),
      className: 'text-[11px] text-muted-foreground whitespace-nowrap',
      sortable: true,
      sortValue: (event) => event.createdAt ?? '',
      render: (event) => (event.createdAt === null ? '' : formatDateTime(event.createdAt)),
    },
  ]

  return (
    <main className="space-y-1" data-audit-log>
      {/* §3.78 - the title block every other screen in the product opens with. This page had
        * none at all: you landed on it and nothing on the screen said what it was. */}
      <PageHeader title={tt('Audit')} description={tt('Každý zápis do centrálnych dát, kto ho urobil a čo sa zmenilo.')} />
      {/* §3.82 - a screen that cannot load its data must not render its empty state.
        * Discarding `isLoading` and `isError` meant *"Žiadne záznamy"* appeared while
        * the request was in flight and again when it failed - so a broken load read as a quiet
        * period, which is §3.52's argument applied to the six screens it did not reach. */}
      {isError && (
        <ReadFailed marker="audit-error"><T>Audit sa nepodarilo načítať.</T></ReadFailed>
      )}
      {isLoading && (
        <div className="py-10 flex justify-center" data-audit-loading>
          <Spinner />
        </div>
      )}


      {/* §3.48 - the page draws the newest 100 and used to say nothing about the rest, so *nothing
        * happened before this* and *we stopped showing you* read identically. Shown only when the
        * two figures disagree: a note on every page is a note nobody reads. */}
      {truncated !== '' && (
        <p className="text-[11px] font-semibold text-amber-500" data-audit-truncated>
          {truncated}
        </p>
      )}
      {!isLoading && !isError && events.length === 0 && (
        <div data-no-events>
          <EmptyState
            icon={<ScrollText className="w-12 h-12" />}
            title={tt('Žiadne záznamy')}
            description={tt('Zmeny, ktoré niekto urobí na platforme, sa objavia tu.')}
          />
        </div>
      )}
      {/* §3.92 - `DataTable`, and this page gains the most from it of any in the Tower. An audit
        * trail is the one screen where *when*, *who* and *what action* are all questions somebody
        * arrives with, and none of them could be sorted: it was a hundred stacked cards in whatever
        * order the server sent. Paging replaces a hundred-row scroll.
        *
        * The three hooks the e2e reads - `data-audit-changes`, `data-audit-first-set` and
        * `data-audit-change` - stay on the row through `rowAttributes` and inside the cells. Those
        * assertions are all *greater than nought* or a slice, so the doubled render cannot break
        * them and nothing needed rescoping here. */}
      {!isLoading && !isError && events.length > 0 && (
        <DataTable<AuditEvent>
          columns={columns}
          data={events}
          rowKey={(event) => event.id}
          rowAttributes={(event) => ({
            'data-audit-event': event.id,
            'data-audit-first-set': isFirstSet(event) ? 'yes' : 'no',
            'data-audit-changes': String(changes(event).length),
          })}
          emptyMessage={tt('Žiadne záznamy')}
          compact
          /*
           * **One page, and derived rather than chosen.** `truncationNote` says *"100 z 347
           * záznamov"* - and its first number **is** this row count. Paging at the default 25 would
           * leave the page claiming a hundred rows are shown while twenty-five are on screen, so the
           * note and the table would contradict each other on the one screen whose whole job is
           * being trustworthy.
           *
           * Tying the page size to the data makes them unable to disagree, whatever the server's cap
           * becomes. The cap is the server's - `truncationNote` exists because it is - and this
           * screen's contract is *show what you were sent and say what you were not*.
           */
          pageSize={Math.max(events.length, 1)}
        />
      )}
    </main>
  )
}
